import { getApp, getApps, initializeApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'

/**
 * FIREBASE — det eksisterende projekt "lykkevaerk".
 * Kun de offentlige web-nøgler står her. Private nøgler (service accounts,
 * Admin SDK-credentials) må aldrig ligge i frontend-koden.
 */
const firebaseConfig = {
  apiKey: 'AIzaSyBWzXnIO1brIw01kFfBPmmUE7OOwfPbhg4',
  authDomain: 'lykkevaerk.firebaseapp.com',
  projectId: 'lykkevaerk',
  storageBucket: 'lykkevaerk.firebasestorage.app',
  messagingSenderId: '333444402082',
  appId: '1:333444402082:web:6ce51de6e68b4e5bbe80fb',
  measurementId: 'G-NSZDNR48EB',
}

const app = getApps().length ? getApp() : initializeApp(firebaseConfig)

export const auth = getAuth(app)
export const db = getFirestore(app)
export const storage = getStorage(app)

// Midlertidig fejlsøgning: bekræfter hvilket Firebase-projekt appen kører imod.
// eslint-disable-next-line no-console
console.info('[Firebase] initialiseret', {
  projectId: app.options.projectId,
  authDomain: app.options.authDomain,
  apiKeySlut: String(app.options.apiKey ?? '').slice(-8),
  apps: getApps().length,
})

/** Trækker Firebase-fejlkoden ud, fx "auth/invalid-credential". */
export function firebaseErrorCode(error: unknown): string {
  return typeof error === 'object' && error && 'code' in error
    ? String((error as { code: string }).code)
    : ''
}

/**
 * Logger den rå Firebase-fejl til konsollen, så den faktiske årsag kan ses
 * under fejlsøgning i stedet for at forsvinde bag en generisk besked.
 */
export function logFirebaseError(context: string, error: unknown): void {
  const code = firebaseErrorCode(error)
  const message = error instanceof Error ? error.message : String(error)
  // eslint-disable-next-line no-console
  console.error(`[Firebase] ${context}`, { code, message, error })
}

/** Oversætter Firebase-fejlkoder til brugervenlige danske beskeder. */
export function firebaseErrorMessage(error: unknown): string {
  const code = firebaseErrorCode(error)
  switch (code) {
    case 'auth/invalid-credential':
      return 'Forkert e-mail eller adgangskode.'
    case 'auth/invalid-email':
      return 'E-mailadressen er ikke gyldig.'
    case 'auth/user-not-found':
      return 'Brugeren findes ikke.'
    case 'auth/wrong-password':
      return 'Forkert adgangskode.'
    case 'auth/user-disabled':
      return 'Brugeren er deaktiveret i Firebase.'
    case 'auth/too-many-requests':
      return 'For mange loginforsøg. Prøv igen senere.'
    case 'auth/network-request-failed':
      return 'Der kunne ikke oprettes forbindelse til Firebase.'
    case 'auth/operation-not-allowed':
      return 'E-mail/password-login er ikke aktiveret i Firebase.'
    case 'auth/unauthorized-domain':
      return 'Dette domæne er ikke godkendt under Authentication → Settings → Authorized domains.'
    case 'auth/api-key-not-valid':
    case 'auth/invalid-api-key':
      return 'Firebase-API-nøglen bliver afvist. Tjek nøglen og dens begrænsninger.'
    case 'auth/requires-recent-login':
      return 'Log ud og ind igen, før du skifter adgangskode.'
    case 'auth/weak-password':
      return 'Den nye adgangskode er for svag. Brug mindst 8 tegn.'
    case 'permission-denied':
      return 'Du har ikke adgang til disse data. Log ind igen.'
    case 'unavailable':
      return 'Der er ingen forbindelse til serveren lige nu. Prøv igen.'
    case 'storage/unauthorized':
      return 'Billedet kunne ikke uploades — adgang nægtet.'
    case 'storage/canceled':
      return 'Upload blev afbrudt.'
    case 'storage/retry-limit-exceeded':
      return 'Uploaden tog for lang tid. Tjek din forbindelse, og prøv igen.'
    default:
      // Ukendt fejl: vis den rigtige kode, så problemet kan diagnosticeres.
      return code
        ? `Der opstod en fejl (${code}).`
        : 'Der opstod en uventet fejl. Prøv igen.'
  }
}
